<template>
	<el-dialog
		:modelValue="open"
		@update:modelValue="$emit('update:open', $event)"
		:title="'Stellungnahme anfordern'"
		@close="handleClose"
		:before-close="(done) => dialogBeforeClose(done, waiting)"
	>
		<TheForm
			:model="request"
			:rules="rules"
			:waiting="waiting"
			:waitingMsg="texts.formErrors.waiting"
			:showErr="showErr"
			:errMsg="errMsg"
			ref="formRef"
			:id="id"
		>
			<el-row>
				<el-col :xs="24" :sm="12">
					<el-form-item label="Kontakt wählen" prop="contactName">
						<el-select
							v-model="request.contactName"
							filterable
							remote
							clearable
							placeholder="Kontakt wählen"
							remote-show-suffix
							:remote-method="doSearch"
							:loading="contactsLoading"
							@change="setContact"
							@clear="
								request = {
									...request,
									id: '',
									email: '',
									gender: '',
									firstName: '',
									lastName: '',
									contactName: '',
								}
							"
							@focus="focusRemoteSearchSelect"
						>
							<el-option v-for="item in filteredContacts" :key="item.label" :label="item.label" :value="item.value" />
						</el-select>
					</el-form-item>
				</el-col>
			</el-row>

			<el-row>
				<el-col :xs="24" :sm="12">
					<el-form-item label="E-Mail-Adresse">
						<el-input v-model="request.email" disabled />
					</el-form-item>
				</el-col>

				<el-col :xs="24" :sm="12">
					<el-form-item label="Anrede">
						<el-select v-model="request.gender" :placeholder="''" disabled>
							<el-option v-for="item in genderOptions" :key="item.value" :label="item.label" :value="item" />
						</el-select>
					</el-form-item>
				</el-col>
			</el-row>

			<el-row>
				<el-col :xs="24" :sm="12">
					<el-form-item label="Vorname">
						<el-input v-model="request.firstName" disabled />
					</el-form-item>
				</el-col>

				<el-col :xs="24" :sm="12">
					<el-form-item label="Nachname">
						<el-input v-model="request.lastName" disabled />
					</el-form-item>
				</el-col>
			</el-row>

			<el-row>
				<el-col :xs="24" :sm="12">
					<el-form-item label="Enddatum" prop="firstValidDate">
						<el-date-picker
							v-model="request.firstValidDate"
							type="date"
							placeholder="TT.MM.JJJJ"
							:format="'DD.MM.YYYY'"
							:disabled-date="disabledDate"
						/>
					</el-form-item>
				</el-col>
			</el-row>

			<el-form-item label="Freitext" prop="question">
				<el-input v-model="request.question" :autosize="{ minRows: 10 }" type="textarea" placeholder="Bitte Text für Stellungnahme hier eingeben." />
			</el-form-item>
		</TheForm>

		<template #footer>
			<div class="button-panel">
				<TheButton type="danger" @buttonclick="save" :disabled="waiting" :loading="waiting">Stellungnahme anfordern</TheButton>
				<TheButton type="default" @buttonclick="handleClose">Abbrechen</TheButton>
			</div>
		</template>
	</el-dialog>
</template>

<script lang="ts">
import { caseRequestStatement } from '@/requests/cases';
import { searchContacts } from '@/requests/searches.js';
import { CaseRequestStatementRequest, Gender, SearchContactRequest, UserResponse } from '@/types/types';
import { dialogBeforeClose, resetForm, validateForm, getEndOfDayWithTime } from '@/utils/functions';
import { checkIfDateInPast } from '@/utils/search/searchUtils';
import type { FormInstance } from 'element-plus';
import { defineComponent, ref } from 'vue';
import { returnFormOptions } from '../../utils/formOptions.js';
import { texts, translateToGerman } from '../../utils/texts';
import TheButton from '../TheButton.vue';
import TheForm from '../TheForm.vue';

interface ElSelectListItem {
	value: UserResponse;
	label: string;
}

interface DialogRequestStatementData {
	request: {
		contactName: string;
		email: string;
		id: string;
		gender: Object;
		firstName: string;
		lastName: string;
		question: string;
		firstValidDate: string | null;
	};
	rules: Object;
	id: string;
	waiting: boolean;
	showErr: boolean;
	errMsg: string;
	filteredContacts: ElSelectListItem[];
	contactsLoading: boolean;
}

export default defineComponent({
	name: 'DialogRequestStatement',
	data(): DialogRequestStatementData {
		return {
			request: {
				contactName: '',
				id: '',
				email: '',
				gender: '',
				firstName: '',
				lastName: '',
				question: '',
				firstValidDate: null,
			},
			rules: {
				contactName: [{ required: true, min: 3, message: texts.formErrors.contactMissing, trigger: 'blur' }],
				question: [
					{ required: true, message: texts.formErrors.reqMissing, trigger: 'blur' },
					{ min: 3, message: texts.formErrors.minChars, trigger: 'blur' },
				],
				firstValidDate: [{ required: true, message: texts.formErrors.reqMissing, trigger: 'blur' }],
			},
			id: 'createStatementForm',
			waiting: false,
			showErr: false,
			errMsg: texts.statements.saveFailed,
			filteredContacts: [] as ElSelectListItem[],
			contactsLoading: false,
		};
	},
	components: {
		TheForm,
		TheButton,
	},
	props: {
		open: {
			type: Boolean,
			required: true,
		},
		caseId: {
			type: String,
			required: true,
		},
	},
	setup() {
		const formRef = ref<FormInstance | null>(null);

		return {
			formRef,
		};
	},
	emits: ['reload', 'update:open'],
	computed: {
		genderOptions() {
			return returnFormOptions('gender');
		},
		texts() {
			return texts;
		},
	},
	methods: {
		async doSearch(val) {
			if (!val) return;

			this.filteredContacts = [];
			this.contactsLoading = true;
			this.request.contactName = val;

			const requestSearchContact = {
				searchTerm: this.request.contactName,
				withUsers: true,
			} as SearchContactRequest;

			console.log('requestSearchContact', requestSearchContact);
			const searchResult = await searchContacts(requestSearchContact, 0);
			if (searchResult.success) {
				this.filteredContacts = searchResult.data.items.map((item) => {
					return {
						value: item,
						label: item.firstName + ' ' + item.lastName,
					};
				});
			}

			this.contactsLoading = false;
		},
		setContact(contact: UserResponse) {
			if (contact) {
				const genderOption = contact.gender === Gender.Male ? { label: 'Herr', value: Gender.Male } : { label: 'Frau', value: Gender.Female };
				this.request = {
					...this.request,
					contactName: contact.firstName + ' ' + contact.lastName,
					id: contact.id,
					email: contact.email,
					gender: genderOption,
					firstName: contact.firstName,
					lastName: contact.lastName,
				};
			}
		},
		focusRemoteSearchSelect() {
			this.request.contactName = '';
		},
		async save() {
			const isValid = await validateForm(this.formRef, this.id);
			if (!isValid) return;

			this.waiting = true;

			let firstValidDate = this.request.firstValidDate;
			if (this.request.firstValidDate) {
				firstValidDate = getEndOfDayWithTime(this.request.firstValidDate);
			}

			const requestStatement = {
				caseId: this.caseId,
				userId: this.request.id,
				firstValidDate: firstValidDate,
				question: this.request.question,
			} as CaseRequestStatementRequest;

			const result = await caseRequestStatement(requestStatement);
			if (result.success) {
				this.handleClose(true);
			} else {
				const msg = translateToGerman(result.error);
				if (msg) {
					this.errMsg = msg;
				}

				this.showErr = true;
			}

			this.waiting = false;
		},
		disabledDate(date) {
			return checkIfDateInPast(date, false);
		},
		dialogBeforeClose,
		handleClose(reload: Boolean) {
			resetForm(this.formRef, this.id);

			this.showErr = false;

			if (reload) {
				this.$emit('reload');
			}
			this.$emit('update:open', false);
		},
	},
});
</script>
